import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, AlertCircle, CheckCircle, Shield, Users, Zap } from "lucide-react";
import authService from "@/services/auth.service";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Alert, AlertDescription } from "./ui/alert";
import { useTranslation } from 'react-i18next';

const ForgotPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Bitte geben Sie eine gültige E-Mail-Adresse ein");
      setIsLoading(false);
      return;
    }

    try {
      const response = await authService.forgotPassword(email);
      if (response.success) {
        setSuccess(true);
      }
    } catch (error: any) {
      if (error.response?.status === 429) {
        setError("Zu viele Anfragen. Bitte versuchen Sie es in 1 Stunde erneut.");
      } else {
        setSuccess(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = () => {
    setSuccess(false);
    setEmail("");
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Side - Dark Sidebar */}
      <div className="hidden lg:flex lg:w-[45%] bg-[#0f172a] relative overflow-hidden flex-col justify-between p-12">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#0F99FD]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-900/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

        {/* Logo */}
        <div className="relative z-10">
          <img src="/brand/vyndesk-logo-light.png" alt="VynDesk Logo" className="h-10 w-auto" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-lg">
          <h1 className="text-4xl font-bold text-white mb-6 leading-tight">
            {t('auth:login.password', 'Passwort')} <span className="text-[#0F99FD]">vergessen?</span>
          </h1>
          <p className="text-lg text-slate-400 mb-8 leading-relaxed">
            Kein Problem! Wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.
          </p>

          <div className="space-y-4">
            <div className="flex items-center gap-3 text-slate-300">
              <div className="h-10 w-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                <Users className="h-5 w-5 text-[#0F99FD]" />
              </div>
              <div>
                <p className="font-medium text-white">{t('auth:side.s1', 'Zentralisierte Verwaltung')}</p>
                <p className="text-sm text-slate-500">{t('auth:side.s1d', 'Alle Tickets an einem Ort')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-slate-300">
              <div className="h-10 w-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                <Zap className="h-5 w-5 text-[#0F99FD]" />
              </div>
              <div>
                <p className="font-medium text-white">{t('auth:side.s2', 'KI-Automatisierung')}</p>
                <p className="text-sm text-slate-500">{t('auth:side.s2d', 'Intelligente Antworten & Routing')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-slate-300">
              <div className="h-10 w-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                <Shield className="h-5 w-5 text-[#0F99FD]" />
              </div>
              <div>
                <p className="font-medium text-white">{t('auth:side.s3', 'Sicher & Zuverlässig')}</p>
                <p className="text-sm text-slate-500">{t('auth:side.s3d', 'Enterprise-Grade Security')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 text-sm text-slate-500">
          © {new Date().getFullYear()} Vyndesk. Alle Rechte vorbehalten.
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col justify-center items-center px-8 sm:px-12 lg:px-16 bg-white">
        <div className="w-full max-w-[400px] space-y-8">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img src="/logo-full.png" alt="VynDesk Logo" className="h-8 w-auto mx-auto" />
          </div>

          {!success ? (
            <>
              <div className="text-center space-y-2">
                <div className="mx-auto h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                  <Mail className="h-7 w-7 text-slate-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">{t('auth:forgot.title', 'Passwort vergessen?')}</h2>
                <p className="text-slate-500 text-sm">
                  Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen.
                </p>
              </div>

              {error && (
                <Alert variant="destructive" className="border-red-100 bg-red-50 text-red-900">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-700">{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-slate-700">{t('auth:register.email', 'E-Mail-Adresse')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder={t('auth:login.emailPh', 'name@company.com')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-11 bg-white border-slate-200 focus:border-[#0F99FD] focus:ring-[#0F99FD]/20 transition-all"
                      required
                      disabled={isLoading}
                      autoFocus
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-[#0F99FD] hover:bg-[#0c84e0] text-white font-semibold transition-all shadow-sm hover:shadow-md"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t('common:x4.sendingDots', 'Senden...')}
                    </span>
                  ) : (
                    "Link zum Zurücksetzen senden"
                  )}
                </Button>
              </form>

              <div className="text-center">
                <p className="text-sm text-slate-500">
                  {t('common:x4.rememberPassword', 'Erinnern Sie sich an Ihr Passwort?')}{" "}
                  <Link to="/login" className="font-semibold text-[#0F99FD] hover:text-[#0c84e0] transition-colors">
                    {t('common:action.backToLogin', 'Zurück zur Anmeldung')}
                  </Link>
                </p>
              </div>
            </>
          ) : (
            <div className="space-y-6">
              <div className="text-center space-y-4">
                <div className="mx-auto h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center">
                  <CheckCircle className="h-7 w-7 text-emerald-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">{t('auth:forgot.sent', 'E-Mail gesendet!')}</h2>
                <p className="text-slate-500 text-sm">
                  {t('common:x4.ifAccountWith', 'Falls ein Konto mit')} <span className="font-semibold text-slate-700">{email}</span> existiert, haben wir einen Link zum Zurücksetzen gesendet.
                </p>
              </div>

              <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 text-sm text-slate-600">
                <p>Bitte überprüfen Sie Ihren Posteingang und ggf. den Spam-Ordner. Der Link ist <strong>1 Stunde</strong> gültig.</p>
              </div>

              <div className="space-y-3">
                <Button
                  className="w-full h-11 bg-[#0F99FD] hover:bg-[#0c84e0] text-white font-semibold"
                  onClick={() => navigate("/login")}
                >
                  {t('common:action.backToLogin', 'Zurück zur Anmeldung')}
                </Button>

                <Button
                  variant="outline"
                  className="w-full h-11"
                  onClick={handleResend}
                >
                  E-Mail erneut senden
                </Button>
              </div>

              <div className="text-center text-sm text-slate-500">
                <p>
                  {t('common:x4.checkSpamFolder', 'Keine E-Mail erhalten? Überprüfen Sie Ihren Spam-Ordner.')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
